import { AsyncPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { injectQueryClient } from '@ngneat/query'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { AngularSplitModule } from 'angular-split'
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer'
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, fromEvent, map, Observable, race, Subject, Subscription, take, timer } from 'rxjs'
import { callResourceTypesToUpload, MAX_AGENDA_SIZE_IN_BYTES } from '../call-document-upload/call-document-upload.component'
import { TranscriptComponent } from '../call/transcript/transcript.component'
import {
  Call,
  CallProcessingStatus,
  CallResource,
  CallsService,
  MetricType,
  Sentence,
  TemplateSourceType,
  computeIsCheckingAgendaValidity,
  computeAgendaUploadStatus,
  computeAgendaFailureMessageKey,
  computeShouldConfirmTemplateReplace,
  computeShouldShowTemplateSourcePicker,
  isAgendaExtractionRejectionVisible,
  AgendaUploadStatus,
} from '../calls.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { DocumentUploadDialogComponent, DocumentUploadDialogInputData } from '../document-upload-dialog/document-upload-dialog.component'
import { LoggerService } from '../logger.service'
import { useMediaTimePrecision } from '../media-player-controller/media-player-controller.component'
import { MediaPlayerComponent } from '../media-player/media-player.component'
import { MetricsService } from '../metrics.service'
import { SpeakerXRayComponent } from '../speaker-x-ray/speaker-x-ray.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { TemplateEditorComponent } from '../template-editor.component/template-editor.component'
import { FileUploadRequest } from '../template-editor.component/template-item/template-item.component'
import { TemplateItem } from '../template-editor.component/types/template.types'
import { TemplatesService } from '../templates.service'
import { ToastService } from '../toast.service'
import { getUploadErrorMessage } from '../uploads.service'
import { UsersService } from '../users.service'
import { DocumentUploadInfo, toSourceDocumentFormat } from '../utils'
import { TemplateSourcePickerComponent } from '../template-source-picker/template-source-picker.component'
import { DocumentUploadService } from '../document-upload.service'
import {
  TemplateImportDialog,
  TemplateImportDialogInputData,
  TemplateImportDialogOutputData,
} from '../template-editor.component/template-import-dialog/template-import-dialog'
import { TemplateSaveAsDialog, TemplateSaveAsDialogOutputData } from '../template-editor.component/template-save-as-dialog/template-save-as-dialog'

const DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH = 25
@Component({
  selector: 'app-call-resources-container',
  standalone: true,
  imports: [
    AsyncPipe,
    TranslatePipe,
    TranslateDirective,
    AngularSplitModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    TranscriptComponent,
    SpeakerXRayComponent,
    SpinnerComponent,
    MediaPlayerComponent,
    NgxExtendedPdfViewerModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    TemplateEditorComponent,
    TemplateSourcePickerComponent,
  ],
  templateUrl: './call-resources-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-resources-container.component.scss',
})
export class CallResourcesContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canEdit!: boolean

  @Input({ required: true })
  userHasEditorAccess!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @ViewChild(MediaPlayerComponent)
  mediaPlayer: MediaPlayerComponent | undefined = undefined

  @ViewChild('templateEditor')
  templateEditor!: TemplateEditorComponent

  @ViewChild('templateSourcePicker')
  templateSourcePicker: TemplateSourcePickerComponent | undefined

  @ViewChild('pdfViewer')
  pdfViewer: NgxExtendedPdfViewerComponent | undefined = undefined

  currentTab: 'MEETING' | 'DOCUMENTS' | 'AGENDA' = 'MEETING'
  documentPdfBlobs: Map<number, Blob> = new Map()
  selectedDocumentId: number | null = null
  currentPdfPage = 1

  usersService = inject(UsersService)
  translateService = inject(TranslateService)
  currentUser$ = this.usersService.currentUser$
  http = inject(HttpClient)

  router = inject(Router)
  route = inject(ActivatedRoute)
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  logger = inject(LoggerService)
  dialog = inject(MatDialog)
  templatesService = inject(TemplatesService)
  documentUploadService = inject(DocumentUploadService)
  queryClient = injectQueryClient()
  templateItems$!: Observable<QueryObserverResult<TemplateItem[], Error>>
  private scrollingInterval = 100
  private callSubscription: Subscription | null = null
  private trackTranscriptIdSubscription: Subscription | null = null
  transcriptScroll$ = new Subject<void>()
  private isAutomatedScrollingOngoing = false
  shouldShowSpeakers$ = this.route.queryParamMap.pipe(
    map((paramMap) => paramMap.get('showSpeakers') === 'true' || paramMap.get('speakerLabelingModeEnabled') === 'true')
  )

  speakerLabelingModeEnabled$ = this.route.queryParams.pipe(map((params) => params['speakerLabelingModeEnabled'] === 'true'))
  resetSpeakersMutation = this.callsService.resetSpeakersMutation()
  reprocessOutputsMutation = this.callsService.reprocessOutputsMutation()
  removeCallResourceMutation = this.callsService.removeResource()
  updateTemplateItemsMutation = this.templatesService.updateTemplateItems()
  loadTemplateMutation = this.templatesService.loadTemplate()
  saveAsTemplateMutation = this.templatesService.saveCallTemplateAsModel()
  importAgendaMutation = this.callsService.importAgenda()
  patchCallMutation = this.callsService.patchMutation()
  shouldNavigateTranscriptBasedOnMediaTime = true
  isTranscriptMaximized = false
  private secondsToEaseOutSentenceClicksWhenPlayingMedia = 3
  maxAgendaSize = MAX_AGENDA_SIZE_IN_BYTES
  isImportingAgenda = false

  get isExtractingAgenda(): boolean {
    return this.call.processingStatus === CallProcessingStatus.GENERATING_AGENDA_ITEMS
  }

  get agendaExtractionProgress(): number | null {
    if (!this.isExtractingAgenda) {
      return null
    }
    return this.call.agendaExtractionProgress
  }

  get isCheckingAgendaValidity(): boolean {
    return computeIsCheckingAgendaValidity(this.call)
  }

  get isAgendaExtractionFailed(): boolean {
    return isAgendaExtractionRejectionVisible(this.call)
  }

  get agendaUploadStatus(): AgendaUploadStatus {
    return computeAgendaUploadStatus({
      isImporting: this.isImportingAgenda,
      isFailed: this.isAgendaExtractionFailed,
      isChecking: this.isCheckingAgendaValidity,
      isUploaded: this.hasAgendaUploaded && this.call.templateSourceType === TemplateSourceType.UPLOAD,
    })
  }

  get isInvalidAgendaDocument(): boolean {
    return this.call.invalidAgendaReason !== null
  }

  get agendaFailureMessageKey(): string | null {
    return computeAgendaFailureMessageKey(this.call.invalidAgendaReason, this.call.agendaExtractionFailed)
  }

  get isCreateTemplateSelected(): boolean {
    return this.call.templateSourceType === TemplateSourceType.CREATE
  }

  get shouldConfirmTemplateReplace(): boolean {
    return computeShouldConfirmTemplateReplace({
      templateSourceType: this.call.templateSourceType,
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isImportingAgenda: this.isImportingAgenda,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
      isCreateTemplateSelected: this.isCreateTemplateSelected,
      templateItemsCount: this.templateEditor?.templateItems.length ?? 0,
    })
  }

  get isPopulatingTemplate(): boolean {
    return this.isExtractingAgenda && !this.isCheckingAgendaValidity
  }

  shouldShowTemplateSourcePicker(itemCount: number): boolean {
    return computeShouldShowTemplateSourcePicker({
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
      hasTemplateItems: this.call.hasTemplateItems,
      itemCount,
      templateSourceType: this.call.templateSourceType,
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['call'] !== undefined && !changes['call'].firstChange) {
      this.preloadAllDocuments()
      this.validateCurrentTab()
    }
  }

  ngOnInit(): void {
    this.trackTranscriptScrolls()
    this.trackSentenceIdUpdates()
    this.scheduleInitialTranscriptScroll()
    this.templateItems$ = this.templatesService.getTemplateItems(this.call.id)
    this.preloadAllDocuments()
    this.initializeTabFromQueryParams()
  }

  private initializeTabFromQueryParams(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab')
    if (tab === 'template') {
      this.currentTab = 'AGENDA'
      this.router.navigate([], { queryParams: { tab: null }, queryParamsHandling: 'merge' })
    }
  }

  private preloadAllDocuments(): void {
    const resourcesWithUrls = this.call.userUploadedResources.filter((resource) => resource.downloadUrl !== null)
    resourcesWithUrls.forEach((resource) => {
      this.loadDocumentPdf(resource.id)
    })
  }

  private selectDefaultDocument(): void {
    const availableResources = this.call.userUploadedResources.filter((resource) => resource.downloadUrl !== null)
    this.selectedDocumentId = availableResources.length > 0 ? availableResources[0].id : null
  }

  private validateCurrentTab() {
    if (this.currentTab === 'DOCUMENTS') {
      const selectedDocumentExists =
        this.selectedDocumentId !== null &&
        this.call.userUploadedResources.some((resource) => resource.id === this.selectedDocumentId && resource.downloadUrl !== null)
      if (selectedDocumentExists) {
        return
      }
      this.selectDefaultDocument()
      this.currentPdfPage = 1
      if (this.selectedDocumentId === null) {
        this.currentTab = 'MEETING'
      } else if (this.selectedDocumentPdfBlob === null) {
        this.loadDocumentPdf(this.selectedDocumentId)
      }
    }
  }

  get selectedDocument() {
    if (this.selectedDocumentId === null) {
      return null
    }
    return this.call.userUploadedResources.find((resource) => resource.id === this.selectedDocumentId) ?? null
  }

  get selectedDocumentPdfBlob(): Blob | null {
    if (this.selectedDocumentId === null) {
      return null
    }
    return this.documentPdfBlobs.get(this.selectedDocumentId) ?? null
  }

  ngOnDestroy() {
    if (this.trackTranscriptIdSubscription !== null) {
      this.trackTranscriptIdSubscription.unsubscribe()
    }
    if (this.callSubscription !== null) {
      this.callSubscription.unsubscribe()
    }
  }

  reprocessLlm() {
    const titleKey = this.call.hasMinutes ? 'CALLS.REPROCESS_OUTPUTS.TITLE' : 'CALLS.REPROCESS_OUTPUTS.TITLE_FIRST_TIME'
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(titleKey),
        description: this.translateService.instant('CALLS.REPROCESS_OUTPUTS.DESCRIPTION', { callId: this.call.id }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ callId: this.call.id })
      }
    })
  }

  switchToMeetingTab() {
    this.currentTab = 'MEETING'
  }

  private trackSentenceIdUpdates() {
    this.route.queryParams
      .pipe(
        filter((params) => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              skipScroll: null,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
          return params['skipScroll'] !== 'true'
        }),
        map((params) => params['sentenceId'] ?? null),
        filter((sentenceId) => sentenceId !== null),
        distinctUntilChanged()
      )
      .subscribe((sentenceId) => {
        setTimeout(() => this.scrollToSentence(sentenceId))
      })
  }

  shouldMaximizeTranscript(call: Call) {
    return this.isTranscriptMaximized || !call.videoUrl
  }

  get currentProgress(): number {
    if (this.mediaPlayer === null || this.mediaPlayer === undefined) {
      return 0
    }
    const value = (this.mediaPlayer.getTime() / this.mediaPlayer.getDuration()) * 100
    if (Number.isNaN(value)) {
      return 0
    }
    return value
  }

  get activeSpeakerId(): string | null {
    if (this.mediaPlayer === null || this.mediaPlayer === undefined) {
      return null
    }

    const currentTime = this.mediaPlayer.getTime()
    const slot = this.call.transcript.findLast((slot) => slot.startTime < currentTime) as any // TODO The types are incorrect for transcript
    const slotSpeakerId = slot?.speaker?.id ?? null
    const speakerId = this.call.speakers.find((speaker) => speaker.speakerId === slotSpeakerId)?.speakerTag ?? null
    return speakerId
  }

  private scheduleInitialTranscriptScroll() {
    const sentenceId = this.route.snapshot.queryParams['sentenceId'] ?? null
    if (sentenceId === null) {
      return
    }
    setTimeout(() => this.scrollToSentence(sentenceId))
  }

  selectTimestamp(timestamp: number) {
    this.logger.info(`Selecting the media time ${timestamp}s.`)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: useMediaTimePrecision(timestamp),
        sentenceId: null,
        highlight: null,
        startMedia: true,
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  selectSentence(sentence: Sentence) {
    this.logger.info(`Selecting the sentence ${sentence.sentenceId} with the media time ${sentence.startTimeSeconds}s.`)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: Math.max(useMediaTimePrecision(sentence.startTimeSeconds) - this.secondsToEaseOutSentenceClicksWhenPlayingMedia, 0),
        sentenceId: sentence.sentenceId,
        endSentenceId: null,
        highlight: true,
        startMedia: true,
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    this.shouldNavigateTranscriptBasedOnMediaTime = true
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_TRANSCRIPT_TO_REPLAY_VIDEO)
  }

  async resetSpeakers(call: Call): Promise<void> {
    await this.resetSpeakersMutation.mutateAsync({ callId: call.id })
    this.toastService.show('CALLS.RESET_SPEAKERS_SUCCESS')
  }

  private trackTranscriptScrolls() {
    this.transcriptScroll$.pipe(debounceTime(this.scrollingInterval)).subscribe(() => {
      if (this.isAutomatedScrollingOngoing) {
        return
      }
      this.logger.info('Disabling the media time tracking for transcript due to a scroll by user.')
      this.shouldNavigateTranscriptBasedOnMediaTime = false
      const sentences = document.querySelectorAll('[data-sentence-id]')
      const sentence =
        Array.from(sentences).find((sentence: any) => {
          const scrollingContainer = document.querySelector('.scrolling-container')!
          return sentence.offsetTop >= scrollingContainer.scrollTop
        }) ?? null
      if (sentence === null) {
        return
      }
      const sentenceId = sentence.getAttribute('data-sentence-id')
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          sentenceId,
          endSentenceId: null,
          highlight: null,
          skipScroll: true,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    })
  }

  scrollToSentence(sentenceId: string): void {
    if (this.isAutomatedScrollingOngoing) {
      this.logger.info(`Skipping scrolling to sentence ${sentenceId} due to automated scrolling being ongoing.`)
      return
    }

    const element = document.querySelector(`.sentence[data-sentence-id="${sentenceId}"]`) ?? null
    if (element === null) {
      this.logger.error(`Skipping scrolling due to not finding the sentence ${sentenceId}.`)
      return
    }
    this.logger.info('Enabling the scrolling lock due to there being an automated scroll ongoing.')
    this.isAutomatedScrollingOngoing = true

    this.logger.info(`The active sentence (${sentenceId}) is not on the screen. Scrolling to the sentence.`, element)
    element.scrollIntoView({ block: 'start', behavior: 'smooth' })
    const subscription = race(this.onScrollComplete(), timer(1000)).subscribe(() => {
      setTimeout(() => {
        this.logger.info('Releasing the scrolling lock as the automated scroll has completed.')
        this.isAutomatedScrollingOngoing = false
      }, this.scrollingInterval)
      subscription.unsubscribe()
    })
  }

  private onScrollComplete() {
    return fromEvent(document.querySelector('.scrolling-container')!, 'scroll').pipe(debounceTime(this.scrollingInterval))
  }

  async handleDocumentsTabClick() {
    if (this.call.userUploadedResources.length > 0) {
      this.currentTab = 'DOCUMENTS'
      if (this.selectedDocumentId === null) {
        this.selectDefaultDocument()
      }
      if (this.selectedDocumentId !== null && this.selectedDocumentPdfBlob === null) {
        await this.loadDocumentPdf(this.selectedDocumentId)
      }
      return
    }
    if (!this.isAuthorizedToManageDocuments) {
      return
    }
    const dialogRef = this.dialog.open(DocumentUploadDialogComponent, {
      width: '400px',
      data: {
        callId: this.call.id,
        title: this.translateService.instant('CALLS.CALL_INFO.DOCUMENT_UPLOAD.AGENDA'),
        documentToUpload: callResourceTypesToUpload['AGENDA'],
        type: 'agenda',
        afterUpload: async (resource: CallResource) => {
          return this.call.userUploadedResources.find(({ id }) => id === resource.id) !== undefined
        },
      } as DocumentUploadDialogInputData,
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result !== null && result !== undefined) {
        await this.onDocumentSelectionChange(result.id)
      }
    })
  }

  handleAgendaTabClick() {
    this.currentTab = 'AGENDA'
  }

  jumpToTranscript() {
    this.shouldNavigateTranscriptBasedOnMediaTime = true
    this.mediaPlayer?.updateTranscriptToMatchMediaTime()
  }

  get displayMeeting() {
    return this.currentTab === 'MEETING'
  }

  get displayDocuments() {
    return this.currentTab === 'DOCUMENTS'
  }

  get displayAgenda() {
    return this.currentTab === 'AGENDA'
  }

  get isAuthorizedToManageDocuments() {
    return this.call.accessLevel === 'FULL_ACCESS' || this.call.accessLevel === 'EDITOR'
  }

  async removeAgenda() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_AGENDA_TITLE'),
        description: this.translateService.instant('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_AGENDA_DESCRIPTION'),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const agenda = this.call.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA') ?? null
        if (agenda === null) {
          return
        }
        await this.removeCallResourceMutation.mutateAsync({
          callId: this.call.id,
          resourceType: agenda.resourceType,
          index: agenda.index,
          extension: agenda.extension,
        })
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.AGENDA_REMOVED_SUCCESS')
        this.currentTab = 'MEETING'
      }
    })
  }

  private async loadDocumentPdf(resourceId: number): Promise<void> {
    const resource = this.call.userUploadedResources.find((resource) => resource.id === resourceId) ?? null
    if (resource === null || resource.downloadUrl === null || this.documentPdfBlobs.has(resourceId)) {
      return
    }

    try {
      const blob = (await firstValueFrom(this.http.get(resource.downloadUrl, { responseType: 'blob' }))) ?? null
      if (blob !== null) {
        this.documentPdfBlobs.set(resourceId, blob)
        this.logger.info(`Document PDF blob loaded successfully for resource ${resourceId}`)
      }
    } catch (error) {
      this.logger.error(`Failed to load document PDF for resource ${resourceId}`, error)
    }
  }

  async onDocumentSelectionChange(resourceId: number) {
    this.selectedDocumentId = resourceId
    this.currentPdfPage = 1
    this.currentTab = 'DOCUMENTS'
    if (!this.documentPdfBlobs.has(resourceId)) {
      await this.loadDocumentPdf(resourceId)
    }
  }

  get allowReprocessingOutputs() {
    return !this.call.isMinutingUpToDate || !this.call.isOutlineUpToDate
  }

  async saveTemplate(items: TemplateItem[]) {
    try {
      const response = await this.updateTemplateItemsMutation.mutateAsync({ callId: this.call.id, items })
      // Apply the id mapping before confirming success: confirmSaveSuccess can synchronously trigger a
      // queued save (pendingSaveAfterCurrent), which builds its payload from _templateItems immediately -
      // if that still has temp ids for what this save just created, the backend treats them as new
      // creates instead of updates, producing duplicates.
      this.templateEditor.applyIdMapping(response.clientIdToServerIdMapping)
      this.templateEditor.confirmSaveSuccess()
    } catch (error) {
      this.logger.error('Failed to save template:', error)
      this.templateEditor.confirmSaveFailure()
    }
  }

  async handleFileUpload(request: FileUploadRequest, callId: number) {
    await this.documentUploadService.uploadCustomDocumentSourceToBlock(callId, request.blockId, request.file, this.templateEditor)
  }

  async handleOpenDocumentEvent(event: { resourceId: number; resourcePageNumber: number }) {
    const resource = this.call.userUploadedResources.find((resource) => resource.id === event.resourceId)

    if (resource === undefined) {
      console.warn(`Could not find user uploaded resource with ID ${event.resourceId} to switch to.`)
      return
    }

    if (resource.downloadUrl === null) {
      console.warn(`Could not switch to user uploaded resource with ID ${event.resourceId} as it has no download URL.`)
      return
    }

    if (this.selectedDocumentId !== resource.id || this.currentTab !== 'DOCUMENTS') {
      await this.onDocumentSelectionChange(resource.id)
    }

    this.currentPdfPage = event.resourcePageNumber
  }

  get papersTabLabel(): string {
    const papersLabel = this.translateService.instant('CALLS.CALL_INFO.DOCUMENT_UPLOAD.PAPERS')
    if (this.selectedDocument === null) {
      return papersLabel
    }
    const fileNameWithoutExtension = this.selectedDocument.fileName.replace(/\.[^/.]+$/, '')
    const truncatedFileName =
      fileNameWithoutExtension.length > DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH
        ? fileNameWithoutExtension.substring(0, DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH) + '...'
        : fileNameWithoutExtension
    return `${papersLabel} (${truncatedFileName})`
  }

  get hasUnsavedTemplateChanges(): boolean {
    return this.templateEditor?.hasUnsavedChanges ?? false
  }

  get hasAgendaUploaded(): boolean {
    return this.call.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
  }

  get uploadedAgendaFileName(): string | null {
    const agendaResource = this.call.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA')
    return agendaResource?.fileName ?? null
  }

  async onUploadConfirmationRequested() {
    const confirmed = await this.showTemplateReplaceConfirmation()
    if (confirmed && this.templateSourcePicker !== undefined) {
      this.templateSourcePicker.openFileDialog()
    }
  }

  private async showTemplateReplaceConfirmation(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALL_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.TITLE'),
        description: this.translateService.instant('CALL_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('CALL_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })
    return await firstValueFrom(dialogRef.afterClosed())
  }

  async onAgendaFileChange(documentUploadInfo: DocumentUploadInfo | null): Promise<void> {
    if (documentUploadInfo === null) {
      return
    }

    this.isImportingAgenda = true
    try {
      await this.importAgendaMutation.mutateAsync({
        callId: this.call.id,
        data: {
          targetResourceExtension: documentUploadInfo.extension,
          uploadKey: documentUploadInfo.key,
          fileName: documentUploadInfo.fileName,
          sourceDocumentFormat: toSourceDocumentFormat(documentUploadInfo.extension),
        },
      })
      this.toastService.show('CALL_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOADED')
    } catch (error) {
      this.logger.error('Failed to upload agenda:', error)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('CALL_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOAD_FAILED')
      }
    } finally {
      this.isImportingAgenda = false
    }
  }

  onLoadSavedTemplate(): void {
    this.templatesService
      .getTemplateModels()
      .pipe(
        filter((result) => result.isSuccess),
        take(1)
      )
      .subscribe((result) => {
        const dialogData: TemplateImportDialogInputData = {
          templates: result.data,
        }
        const dialogRef = this.dialog.open(TemplateImportDialog, {
          width: '500px',
          data: dialogData,
        })

        dialogRef.afterClosed().subscribe((dialogResult: TemplateImportDialogOutputData | null) => {
          if (dialogResult !== null && dialogResult !== undefined) {
            this.loadTemplateFromModel(dialogResult.templateId)
          }
        })
      })
  }

  private async loadTemplateFromModel(templateId: number): Promise<void> {
    this.templateEditor.clearUnsavedChanges()
    await this.loadTemplateMutation.mutateAsync({
      callId: this.call.id,
      templateId,
    })
  }

  async createBlankTemplate(): Promise<void> {
    await this.updateTemplateItemsMutation.mutateAsync({
      callId: this.call.id,
      items: [],
    })
    this.toastService.show('CALL_SETUP.TEMPLATE_SOURCE.BLANK_TEMPLATE_CREATED')
  }

  onSaveAsTemplate(items: TemplateItem[]): void {
    const dialogRef = this.dialog.open(TemplateSaveAsDialog, {
      width: '400px',
    })

    dialogRef.afterClosed().subscribe(async (result: TemplateSaveAsDialogOutputData | null) => {
      if (result !== null) {
        await this.saveAsTemplateMutation.mutateAsync({
          organizationId: this.call.organizationId,
          name: result.name,
          items,
        })
      }
    })
  }
}
